// Shared constants and types used by both worker (ingest) and frontend.
// GaugeType and Measurement values must match what Hilltop returns and what
// build-registry writes into data/site-registry.json.

// ── Gauge type ────────────────────────────────────────────────────────────────

export const GaugeType = {
  RAINFALL: "rainfall",
  RIVER: "river",
} as const;

export type GaugeType = (typeof GaugeType)[keyof typeof GaugeType];

// ── Display type ──────────────────────────────────────────────────────────────
// Extends GaugeType with visual sub-categories used for map icons and styling.
// Wells are river-measurement sites whose site name starts with "Well." —
// they use a distinct icon and no alert-level colouring.

export const DisplayType = {
  RAINFALL: "rainfall",
  RIVER: "river",
  WELL: "well",
} as const;

export type DisplayType = (typeof DisplayType)[keyof typeof DisplayType];

// ── Measurement names ─────────────────────────────────────────────────────────
// Exact strings as returned by Hilltop and stored in the site registry.

export const Measurement = {
  // Rainfall gauges
  RAINFALL: "Rainfall",
  RAINFALL_DAILY: "Rainfall Daily",
  RAINOMATIC: "Rainomatic",

  // River gauges
  STAGE: "Stage",
  STAGE_GAUGING: "Stage [Gauging Results]",
  CORRELATIVE_FLOW: "Correlative Flow",
  DEPTH_FROM_MEASURING_POINT: "Depth From Measuring Point [Manual Water Level]",
} as const;

export type Measurement = (typeof Measurement)[keyof typeof Measurement];

// ── Alert level ───────────────────────────────────────────────────────────────

export type AlertLevel = "normal" | "watch" | "high";

// ── Groundwater level ─────────────────────────────────────────────────────────
// Classification relative to historical monthly percentile bands (p25/p75).
// null = insufficient historical data (<5 years per calendar month).

export type GroundwaterLevel = "low" | "normal" | "high";

// ── Rainfall trend ────────────────────────────────────────────────────────────
// Computed from the past hour's readings (two 30-min halves compared).

export type RainfallTrend = "increasing" | "stable" | "decreasing";

// ── Site history ──────────────────────────────────────────────────────────────

export type SiteHistory = {
  unit: string;
  measurement?: string; // stored during ingest; enables on-demand Hilltop fetches beyond KV window
  readings: Array<{ recorded_at: string; value: number }>;
};

// ── Gauge feature properties ──────────────────────────────────────────────────

export type GaugeProperties = {
  site_id: string;
  site_name: string;
  gauge_type: GaugeType;
  display_type: DisplayType;
  measurement: string;
  value: number;
  unit: string;
  recorded_at: string;
  alert_level: AlertLevel | null;
  watch_level?: number; // Stage threshold (mm): Normal → Watch; only set for river sites with scraped thresholds
  high_level?: number;  // Stage threshold (mm): Watch → High
  stale?: boolean;
  multi_gauge?: boolean; // True when this site has more than one gauge type (e.g. rainfall + river co-located)
  trend?: RainfallTrend; // Rainfall only: direction of change over the past hour
  groundwater_level?: GroundwaterLevel | null; // Well only: classification relative to monthly p25/p75 bands
};

// ── Lookup helpers ────────────────────────────────────────────────────────────

export const RAINFALL_MEASUREMENTS: ReadonlySet<string> = new Set([
  Measurement.RAINFALL,
  Measurement.RAINFALL_DAILY,
  Measurement.RAINOMATIC,
]);

export const RIVER_MEASUREMENTS: ReadonlySet<string> = new Set([
  Measurement.STAGE,
  Measurement.STAGE_GAUGING,
  Measurement.CORRELATIVE_FLOW,
  Measurement.DEPTH_FROM_MEASURING_POINT,
]);
